import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as CheckMarkIcon } from 'assets/icons/checkmark.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as RefreshIcon } from 'assets/icons/refresh.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import IconButton from 'components/IconButton/IconButton';
import Loader from 'components/Loader/Loader';
import Panel from 'components/Panel/Panel';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TableSort from 'components/Table/TableSort';
import Tooltip from 'components/Tooltip/Tooltip';
import { MATERIALITY_ASSESSMENT_HEAD_CELLS } from 'constants/dataSets';
import { SORT } from 'constants/formFields';
import { MATERIALITY_ASSESSMENT_STATUS, TASK_SORT_BY } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { getTextDate } from 'utils/formattedData';

import type ITableMaterialityAssessment from './interfaces/ITableMaterialityAssessment';

import styles from './scss/TableMaterialityAssessment.module.scss';

function TableMaterialityAssessment({
  control, items, onSortChange, isLoading, onEdit, onDelete,
  hasNext, onGetMoreItems, handleApprove, handleReject, showAction, isDisabledAccess, isSignOff,
  isViewOnlyOrSignOff,
}: ITableMaterialityAssessment) {
  const { t } = useTranslation();

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const threshold = 300;
      const isNearBottom = scrollTop + windowHeight >= documentHeight - threshold;
      if (isNearBottom && hasNext && !isLoading && onGetMoreItems) {
        onGetMoreItems();
      }
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasNext, isLoading, onGetMoreItems]);

  return (
    <Panel className={styles.panel}>
      {items.length > 0 ? (
        <Table className={styles.table}>
          <TableHead className={styles.header}>
            {MATERIALITY_ASSESSMENT_HEAD_CELLS.filter(({ id }) => (
              !isDisabledAccess || id !== 'actions'
            )).map(({
              id, label, withSort,
            }) => (withSort ? (
              <Controller
                key={id}
                name={SORT}
                control={control}
                render={({
                  field: { value },
                }) => (
                  <TableCell head className={styles.tableCell}>
                    <TableSort
                      active={id === value.orderKey}
                      onClickSort={() => onSortChange(id === TASK_SORT_BY.CREATED ? TASK_SORT_BY.CREATED_AT : id)}
                    >
                      <span>{t(label)}</span>
                    </TableSort>
                  </TableCell>
                )}
              />
            ) : (
              <TableCell
                head
                className={styles.tableCell}
                key={id}
              >
                <div>
                  <span>{t(label)}</span>
                </div>
              </TableCell>
            )))}
          </TableHead>
          <TableBody className={styles.body}>
            {isLoading ? <Loader isPageLoading isFullLoader={false} /> : items?.map((itemMA) => (
              <TableRow key={[itemMA.id, itemMA.name].join('_')}>
                <TableCell className={styles.nameCell}>
                  <Button
                    title={itemMA?.name}
                    onClick={isViewOnlyOrSignOff
                      || (itemMA.status !== MATERIALITY_ASSESSMENT_STATUS.APPROVED && !isSignOff)
                      ? () => onEdit(itemMA.id) : undefined}
                    className={clsx(styles.buttonName, {
                      [styles.disabled]: (itemMA.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
                        || isSignOff) && !isDisabledAccess && !isViewOnlyOrSignOff,
                    })}
                    variant={BUTTON_VARIANT.ghost}
                  />
                </TableCell>
                <TableCell>
                  <div className={styles.createdCell}>
                    <span className={styles.titleCreated}>
                      {getTextDate(itemMA.createdAt)}
                    </span>
                    <span className={styles.nameCreated}>
                      {`by ${itemMA.createdBy.firstName} ${itemMA.createdBy.lastName}`}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={styles.createdCell}>
                    <span className={styles.titleCreated}>
                      {getTextDate(itemMA.updatedAt)}
                    </span>
                    { itemMA?.updatedBy?.firstName ? (
                      <span className={styles.nameCreated}>
                        {`by ${itemMA.updatedBy.firstName} ${itemMA.updatedBy.lastName}`}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  <div className={styles.statusCell}>
                    <span className={`${styles.statusText} ${styles[itemMA.status]}`}>
                      <span>{t(`materialityAssessment.statusOptions.${itemMA.status}`)}</span>
                    </span>
                  </div>
                </TableCell>
                {!isDisabledAccess ? (
                  <TableCell className={styles.actionCell}>
                    <div className={styles.actionButtons}>
                      {itemMA.status !== MATERIALITY_ASSESSMENT_STATUS.APPROVED
                        && itemMA.status !== MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW
                        && !isSignOff && (
                        <>
                          <Tooltip hint={t('materialityAssessment.edit')}>
                            <IconButton
                              icon={<PencilIcon />}
                              onClick={() => onEdit(itemMA.id)}
                            />
                          </Tooltip>
                          <Tooltip hint={t('common.delete')}>
                            <IconButton
                              icon={<TrashIcon />}
                              onClick={() => onDelete(itemMA.id)}
                            />
                          </Tooltip>
                        </>
                      )}
                      {showAction
                      && itemMA.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW && isSignOff ? (
                        <>
                          <Tooltip hint={t('materialityAssessment.approve')}>
                            <IconButton
                              icon={<CheckMarkIcon />}
                              onClick={() => handleApprove(itemMA.id)}
                              className={styles.iconButton}
                            />
                          </Tooltip>
                          <Tooltip hint={t('materialityAssessment.returnForWork')}>
                            <IconButton
                              icon={<RefreshIcon />}
                              onClick={() => handleReject(itemMA.id)}
                              className={styles.iconButton}
                            />
                          </Tooltip>
                        </>
                        ) : null}
                    </div>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : <EmptyList text={emptyListText} />}
    </Panel>
  );
}

export default TableMaterialityAssessment;
